
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import * as LucideIcons from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ProductFormDialogProps {
  open: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  onIconPickerOpen: () => void;
  selectedIcon: string;
  setSelectedIcon: (icon: string) => void;
  form: UseFormReturn<any>;
  products: { id: string; name: string }[];
}

const ProductFormDialog = ({
  open,
  isLoading,
  onClose,
  onSubmit,
  onIconPickerOpen,
  selectedIcon,
  form,
  products
}: ProductFormDialogProps) => {

  const IconComponent = selectedIcon ? (LucideIcons as any)[selectedIcon] : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {form.getValues('id') ? "Edit Featured Product" : "Add Featured Product"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            <FormField
              control={form.control}
              name="icon_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onIconPickerOpen}
                        className="w-full justify-start gap-2"
                      >
                        {IconComponent && <IconComponent className="h-4 w-4" />}
                        {selectedIcon || "Select Icon"}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center justify-between">
                    Product Name
                    <span className={`text-xs ${field.value?.length > 25 ? 'text-destructive' : field.value?.length > 20 ? 'text-warning' : 'text-muted-foreground'}`}>
                      {field.value?.length || 0}/30
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Ethereal Diamond" 
                      maxLength={30}
                      {...field} 
                    />
                  </FormControl>
                  {field.value?.length > 25 && (
                    <p className="text-xs text-muted-foreground">
                      Tip: Shorter names display better on mobile devices
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center justify-between">
                    Category
                    <span className={`text-xs ${field.value?.length > 15 ? 'text-destructive' : field.value?.length > 12 ? 'text-warning' : 'text-muted-foreground'}`}>
                      {field.value?.length || 0}/20
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Necklaces" 
                      maxLength={20}
                      {...field} 
                    />
                  </FormControl>
                  {field.value?.length > 15 && (
                    <p className="text-xs text-muted-foreground">
                      Consider shorter category names for better display
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="short_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center justify-between">
                    Short Description (Optional)
                    <span className={`text-xs ${field.value?.length > 80 ? 'text-destructive' : field.value?.length > 60 ? 'text-warning' : 'text-muted-foreground'}`}>
                      {field.value?.length || 0}/100
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Limited time offer - Get 30% off!" 
                      maxLength={100}
                      rows={2}
                      {...field} 
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    This description will appear below the price in the product card
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="product_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Link to Real Product (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value ?? 'none'}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a product to link" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">Active</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={isLoading}>
                {form.formState.isSubmitting ? "Saving..." : (form.getValues('id') ? "Update Product" : "Add Product")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
export default ProductFormDialog;
